import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { Box } from "@mui/material";

interface LogoLoaderProps {
  /**
   * Size of the loader logo
   * @default "medium"
   */
  size?: "small" | "medium" | "large";
  /**
   * Show backdrop overlay
   * @default false
   */
  showBackdrop?: boolean;
  /**
   * Custom size in pixels (overrides size prop)
   */
  customSize?: number;
  /**
   * Loading text to display below logo
   */
  text?: string;
  /**
   * Full screen loader
   * @default false
   */
  fullScreen?: boolean;
  /**
   * Custom className for container
   */
  className?: string;
  /**
   * Animation speed
   * @default "normal"
   */
  speed?: "slow" | "normal" | "fast";
  /**
   * Show loader
   * @default true
   */
  show?: boolean;
}

const LogoLoader: React.FC<LogoLoaderProps> = ({
  size = "medium",
  showBackdrop = false,
  customSize,
  text,
  fullScreen = false,
  className = "",
  speed = "normal",
  show = true,
}) => {
  const sizeMap = {
    small: 95,
    medium: 145,
    large: 220,
  };

  const logoSize = customSize || sizeMap[size];

  const speedMap = {
    slow: 2,
    normal: 1.5,
    fast: 1,
  };

  const duration = speedMap[speed];

  // Logo animation variants
  const logoVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Pulse animation for logo
  const pulseVariants = {
    animate: {
      scale: [1, 1.05, 1],
      opacity: [1, 0.8, 1],
      transition: {
        duration: duration,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  // Rotating circle animation around logo
  const circleVariants = {
    animate: {
      rotate: 360,
      transition: {
        duration: duration * 2,
        repeat: Infinity,
        ease: "linear",
      },
    },
  };

  // Text animation
  const textVariants = {
    initial: {
      opacity: 0,
      y: 10,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3,
        duration: 0.4,
      },
    },
  };

  // Backdrop animation
  const backdropVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const containerClasses = fullScreen
    ? "fixed inset-0 z-[9999]"
    : "relative w-full h-full";

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={`${containerClasses}   flex items-center justify-center ${className}`}
          variants={backdropVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {showBackdrop && (
            <motion.div
              className="absolute inset-0 bg-white/80 backdrop-blur-sm"
              variants={backdropVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            />
          )}

          <Box className="flex flex-col items-center justify-center gap-2 relative z-10">
            {/* Rotating circle indicator */}
            <Box className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="absolute border-2 border-secondary border-t-transparent rounded-full"
                style={{
                  width: logoSize + 40,
                  height: logoSize + 40,
                }}
                variants={circleVariants}
                animate="animate"
              />
            </Box>

            {/* Logo with pulse animation */}
            <motion.div
              variants={logoVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <motion.div
                variants={pulseVariants}
                animate="animate"
                className="relative"
              >
                <Image
                  src="/icons/logo.png"
                  alt="Loading..."
                  width={logoSize}
                  height={logoSize * 0.304} // Aspect ratio: 38/125 = 0.304
                  priority
                  className="object-contain rounded-full "
                />
              </motion.div>
            </motion.div>

            {/* Optional loading text */}
            {text && (
              <motion.div
                variants={textVariants}
                initial="initial"
                animate="animate"
                className="text-[#194AF9] font-medium text-base"
              >
                {text}
              </motion.div>
            )}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoLoader;
